/* eslint-disable react/function-component-definition */
/* Global imports. */
import React, { useRef, useEffect, useState } from "react";
import { nanoid } from "nanoid";
import { Link } from "gatsby";
import { StaticQuery, graphql } from "gatsby";
import { getFormat } from "@src/onix";
import { PanMacmillanEdition } from "@src/models/panmacmillan";
import { nonPanMacFormat } from "@src/constants";
import { useSiteMetadata } from "@src/hooks/useSiteMetaData";


interface Props {
    isbn: string;
    author: string;
    title: string;
    format: string;
    imprint: string;
    isPreOrder: boolean;
    edition?: PanMacmillanEdition;
    referringComponentID?: string;
    referringComponent?: string;
    isPanMacmillanTitle: "yes" | "no";
    referringSlug?: string;
    isQuickPreview?: boolean;
}

function clickBuyLink(event) {
    const $book_title = event.target.parentElement.getAttribute("data-title");
    const $book_imprint = event.target.parentElement.getAttribute("data-imprint");
    const $book_format = event.target.parentElement.getAttribute("data-format");

    if (typeof window !== undefined) {
        window.dataLayer = window.dataLayer || [];

        $book_format &&
            $book_title &&
            window.dataLayer.push({
                event: "gatsbyGAEvent",
                category: "Buy Click - Format",
                action: $book_format,
                label: $book_title,
            });
        $book_imprint &&
            $book_title &&
            window.dataLayer.push({
                event: "gatsbyGAEvent",
                category: "Buy Click - Imprint",
                action: $book_imprint,
                label: $book_title,
            });
        $book_title &&
            window.dataLayer.push({
                event: "gatsbyGAEvent",
                category: "Buy Click - Title",
                action: $book_title,
                label: "Book Title",
            });
        $book_title &&
            window.dataLayer.push({
                event: "gatsbyGAEvent",
                category: "Buy Click - Retailer",
                action: event.target.innerText,
                label: $book_title,
            });
        window.dataLayer.push({
            event: "gatsbyGAEvent",
            category: "conversion",
            conversionValue: { send_to: "AW-1004159637/EOR9CNDUheoCEJWF6d4D" },
        });
    }
}

const Buy = ({
    isbn,
    author,
    title,
    format,
    imprint,
    isPreOrder,
    referringComponentID,
    referringComponent,
    isPanMacmillanTitle,
    referringSlug,
}: Props) => {
    const audioFormatArray = ["AJ", "AN", "AO", "AZ"];
    const ebookFormatArray = ["EA", "EB", "EC", "ED"];
    const audioCodename = "audio";
    const ebookCodename = "ebook";
    const bookCodename = "book";
    const allCodename = "all";
    const buyRef = useRef<HTMLDivElement>(null);
    const bookBuyInitialized = useRef(false);
    const [componentLoaded, setComponentLoaded] = useState(false);
    const [isAndroid, setIsAndroid] = useState(false);
    const [isClient, setIsClient] = useState(false);
    let psWigetClassName =
        "ps-widget-button !pl-4 !pr-8 !py-3 h-[3rem] text-left  !font-bold  !rounded-md bg-green hover:bg-green-light border-solid border border-green hover:border-green-light text-white !block !visible overflow-hidden";
    if (componentLoaded) {
        psWigetClassName += " ps-widget";
    }
    const gid_staging = "1747348177|98662159|1238106958|507149691|1735004046|1104167254|2102203900|752093624|1904032440|1526158128|1530343036|1750425461|97807243|1340286978|494875718|928268406|331173270|96356084|1092832434|1541240781|1698628610|382673440|905646158|363360807|897407378|806689730|1121805801|315513074|1209080974|576546111|2131456381";
    
    const gid_production = [
        "1747348177", // Amazon
        "98662159",   // Apple
        "1238106958", // Audible
        "507149691",  // Barnes & Noble
        "1735004046", // Blackwells
        "895705092",  // Books of Wonder
        "2102203900", // Books-a-million
        "752093624",  // Bookshop.org
        "1904032440", // Booktopia
        "1526158128", // Eason
        "1530343036", // eBooks.com
        "1750425461", // Exclusive Books
        "97807243",   // Flipkart
        "1245008856", // Forbidden Planet
        "1340286978", // Foyles
        "494875718",  // Google
        "928268406",  // Hive
        "1634036801", // IndieBound
        "96356084",   // Indigo
        "1092832434", // Kobo
        "1541240781", // Loot
        "1698628610", // Mighty Ape
        "382673440",  // Powell's
        "905646158",  // SapnaOnline
        "1933971722", // Spotify
        "1371306123", // Target
        "576546111",  // TGJones
        "806689730",  // The Book Depository
        "99353802",   // Walmart
        "315513074",  // Waterstones
        "1209080974", // Whitcoulls
        "2131456381", // Wordery
    ].join("|");

    const { useBookBuy, bookBuyApiKey, useBookBuyStaging } = useSiteMetadata();

    useEffect(() => {
        setIsClient(true);
    }, []);


    function setBuyLink(link: string) {
        return link
            .replace("['isbn']", isbn)
            .replace("['title']", title)
            .replace("['author']", author)
            .replace(" ", "%20");
    }

    if (useBookBuy) {
        useEffect(() => {
            // Only run if useBookBuy is true, client-side, and BookBuy script is loaded
            if (useBookBuy && isClient && typeof window !== 'undefined' && window.buylinkEmbed && typeof window.buylinkEmbed.buildBuyLinks === 'function') {
                // Use a small delay to ensure the DOM element is available after React render/hydration
                const timer = setTimeout(() => {
                    // Check if the specific element needs initialization (lacks 'loaded' class)
                    if (buyRef.current) {
                        const buyLinkDiv = buyRef.current.querySelector('.buy-link'); // BookBuy adds 'buy-link' class via its script
                        if (buyLinkDiv && !buyLinkDiv.classList.contains('loaded')) {
                            window.buylinkEmbed.buildBuyLinks();
                        } else if (!buyLinkDiv) {
                            // If the .buy-link div isn't even there yet, try running buildBuyLinks
                            window.buylinkEmbed.buildBuyLinks();
                        }
                    }
                }, 150); // Adjust delay if necessary

                // Cleanup timeout on unmount or before re-running
                return () => clearTimeout(timer);
            }
        }, [useBookBuy, isClient, isbn, bookBuyApiKey]);
    }

    useEffect(() => {
        var androidResult = /Android/i.test(navigator.userAgent);
        setIsAndroid(androidResult);
    }, []);

    const SelectOptions = ({ buyLinks }) =>
        buyLinks &&
        buyLinks.map(function (buyLink) {
            if (buyLink.elements.link_format_mobile.value !== "") {
                if (!isAndroid || !buyLink.elements.link_format_mobile.value.match(/itunes.apple/)) {
                    return (
                        <>
                            <a
                                className="!no-underline leading-[35px] hide-desktop block px-4 hover:!bg-green-light text-grey-dark hover:!text-white text-start"
                                key={nanoid()}
                                data-slug={setBuyLink(buyLink.elements.link_format_mobile.value)}
                                href={setBuyLink(buyLink.elements.link_format_mobile.value)}
                                onClick={clickBuyLink}
                                target="_blank"
                                role="menuitem"
                                data-ref-component-type={referringComponent}
                                data-ref-component-id={referringComponentID}
                                data-ref-slug={referringSlug}
                                data-ispanmac={isPanMacmillanTitle}
                            >
                                {buyLink.elements.name && buyLink.elements.name.value}
                            </a>
                            <a
                                className="!no-underline leading-[35px] show-desktop block px-4 hover:bg-green-light text-grey-dark hover:!text-white text-start"
                                key={nanoid()}
                                data-slug={setBuyLink(buyLink.elements.link_format.value)}
                                href={setBuyLink(buyLink.elements.link_format.value)}
                                onClick={clickBuyLink}
                                target="_blank"
                                role="menuitem"
                                data-ref-component-type={referringComponent}
                                data-ref-component-id={referringComponentID}
                                data-ref-slug={referringSlug}
                                data-ispanmac={isPanMacmillanTitle}
                            >
                                {buyLink.elements.name && buyLink.elements.name.value}
                            </a>
                        </>
                    );
                }
            } else {
                if (!isAndroid || !buyLink.elements.link_format.value.match(/itunes.apple/)) {
                    return (
                        <a
                            className="!no-underline leading-[35px] block px-4 hover:bg-green-light text-grey-dark hover:!text-white text-start"
                            key={nanoid()}
                            data-slug={setBuyLink(buyLink.elements.link_format.value)}
                            href={setBuyLink(buyLink.elements.link_format.value)}
                            onClick={clickBuyLink}
                            target="_blank"
                            role="menuitem"
                            data-ref-component-type={referringComponent}
                            data-ref-component-id={referringComponentID}
                            data-ref-slug={referringSlug}
                            data-ispanmac={isPanMacmillanTitle}
                        >
                            {buyLink.elements.name && buyLink.elements.name.value}
                        </a>
                    );
                }
            }
        });

    const FilterBuyLinksByFormat = (buyLinks, format: string) => {
        if (audioFormatArray.includes(format)) {
            return buyLinks.filter(
                (buyLink) =>
                    buyLink.elements.link_type.value[0] &&
                    buyLink.elements.link_type.value[0].codename === audioCodename,
            );
        } else if (ebookFormatArray.includes(format)) {
            return buyLinks.filter(
                (buyLink) =>
                    buyLink.elements.link_type.value[0] &&
                    buyLink.elements.link_type.value[0].codename === ebookCodename,
            );
        } else {
            return buyLinks.filter(
                (buyLink) =>
                    (buyLink.elements.link_type.value[0] &&
                        buyLink.elements.link_type.value[0].codename === bookCodename) ||
                    (buyLink.elements.link_type.value[0] &&
                        buyLink.elements.link_type.value[0].codename === allCodename),
            );
        }
    };

    return (
        <StaticQuery
            query={graphql`
                query {
                    allKontentItemBuyNowLink {
                        nodes {
                            elements {
                                name {
                                    value
                                }
                                link_format {
                                    value
                                }
                                link_type {
                                    value {
                                        name
                                        codename
                                    }
                                }
                                link_format_mobile {
                                    value
                                }
                            }
                        }
                    }
                }
            `}
            render={(data) => {

                const showStandardDropdown = isClient && !useBookBuy;
                const showBookBuy = isClient && useBookBuy;
                const showPlaceholder = !isClient; 

                // Placeholder styles should mimic the button to prevent layout shift
                const placeholderClasses = "pl-4 pr-8 py-3 h-[3rem] text-left font-bold rounded-md bg-green text-white block invisible"; // Invisible placeholder
                return (
                <>
                    <div className="relative" ref={buyRef} suppressHydrationWarning>
                        {showPlaceholder ? (
                                <div className={placeholderClasses}>
                                    <span className="inline-block pb-[0.75rem]">
                                        {isPreOrder ? "Pre-order" : "Buy"}
                                    </span>
                                    <span className="whitespace-pre inline-block">
                                        {isPreOrder ? " now" : " the book"}
                                    </span>
                                </div>
                        ): showStandardDropdown ? (

                            <div
                                key={`buy_${isbn}`}
                                className="group relative cursor-pointer pr-8 text-white rounded-md sm:max-w-none bg-green hover:bg-green-light dropdown-arrow select--buy border border-green hover:border-green-light"
                                data-component="buy-now"
                                data-ref-component-type={referringComponent}
                                data-ref-component-id={referringComponentID}
                                data-ispanmac={isPanMacmillanTitle}
                                data-ref-slug={referringSlug}
                                tabIndex={1}
                            >
                                <button
                                    tabIndex={0}
                                    aria-label={isPreOrder ? "Pre-order now - " + title : "Buy the book - " + title}
                                    aria-haspopup="true"
                                    className="whitespace-break-spaces pl-4 block py-3 w-full appearance-none text-left font-bold overflow-hidden h-[3rem] text-[1.125rem]"
                                    key={`buy_initial_${isbn}`}
                                >
                                    <span className="inline-block pb-[0.75rem]">
                                        {isPreOrder ? "Pre-order" : "Buy"}
                                    </span>
                                    <span className="whitespace-pre inline-block">
                                        {isPreOrder ? " now" : " the book"}
                                    </span>
                                </button>
                                <div
                                    className="group-focus-within:w-full group-focus-within:h-full absolute top-[0] left-[0] invisible group-focus-within:visible focus:pointer-events-none dropdown-hide"
                                    tabIndex={1}
                                ></div>
                                <div
                                    className="dropdown group-focus-within:visible group-focus-within:opacity-100 group-focus-within:max-h-screen group-focus-within:bg-green-light, group-focus-within:border-green-light, border-solid border border-green shadow-lg absolute invisible transition-opacity duration-700 max-h-0 opacity-0 overflow-hidden whitespace-nowrap self-start bg-white rounded-md min-w-full z-[10]"
                                    role="menu"
                                    aria-orientation="vertical"
                                    data-isbn={isbn}
                                    data-imprint={imprint}
                                    data-format={format !== nonPanMacFormat ? getFormat(format) : `"${format}"`}
                                    data-title={title}
                                    data-author={author}
                                >
                                    <SelectOptions
                                        buyLinks={FilterBuyLinksByFormat(data.allKontentItemBuyNowLink.nodes, format)}
                                    />
                                </div>
                            </div>
                        ) : showBookBuy ? (
                            <div className={'buy-link' + (isPreOrder ? ' pre-order' : '')} data-pk="Isbn" data-ls="1" data-gid={useBookBuyStaging ? gid_staging : gid_production} data-pid={isbn} data-pt={format} data-q={`${title} - ${author}`} data-component="bookbuy-placeholder"></div>
                        ) : (
                            <div className={psWigetClassName + (isPreOrder ? ' pre-order' : '')}></div>
                        )}
                    </div>
                </>
            );}}
        />
    );
};

export default Buy;
